class FacetFiltersForm extends HTMLElement {
  static filterData = [];
  static searchParamsInitial = window.location.search.slice(1);
  static searchParamsPrev = window.location.search.slice(1);

  static updateProductCount() {
    const productCount = document.querySelectorAll('.grid__item.product-card').length;
    const counterWrapper = document.querySelector('.product-type .facet__input:checked');

    if (counterWrapper) {
      const counterPlaceholder = counterWrapper.parentElement.querySelector('.facet__count');
      if (counterPlaceholder) {
        // Detect Safari
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari && productCount.toString().length === 1) {
          // Add spaces for Safari when productCount has only 1 digit
          counterPlaceholder.innerHTML = `( ${productCount} )`;
        } else {
          // Default case
          counterPlaceholder.innerHTML = `(${productCount})`;
        }
      }
    }
  }

  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('change', this.debouncedOnSubmit.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapper');
    if (facetWrapper) {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    // Update the product count on the first load
    setTimeout(() => {
      FacetFiltersForm.updateProductCount();
    }, 100);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);

    document.addEventListener('DOMContentLoaded', () => {
      // Sticky filter logic
      FacetFiltersForm.initStickyFilters();

      // Initialize filter toggles and checkbox handlers
      FacetFiltersForm.initFilterToggles();

      // Set up facet click listeners for animation
      FacetFiltersForm.initFacetClickListeners();
    });
  }

  static initStickyFilters() {
    const productType = document.querySelector('.product-type');
    const useCase = document.querySelector('.use-case');
    const accessoryType = document.querySelector('.accessory-type');
    const announcementBar = document.querySelector('.announcement-bar');

    // Function to update the sticky state of an element
    const updateStickyState = (isIntersecting, element) => {
      if (!element || element.style.display === 'none') return;

      const announcementBarHeight = announcementBar ? announcementBar.clientHeight : 0;

      if (!isIntersecting) {
        // Reserve the height of the element before making it fixed
        const elementHeight = element.offsetHeight;
        const elementParent = element.parentElement;

        // Add a placeholder with the same height to prevent layout shift
        if (!elementParent.querySelector('.sticky-placeholder')) {
          const placeholder = document.createElement('div');
          placeholder.classList.add('sticky-placeholder');
          placeholder.style.height = `${elementHeight}px`;
          elementParent.insertBefore(placeholder, element.nextSibling);
        }

        element.classList.add('fixed');
        element.style.top = `${announcementBarHeight}px`;
      } else {
        element.classList.remove('fixed');
        element.style.top = '';

        // Remove the placeholder when no longer fixed
        const placeholder = element.parentElement.querySelector('.sticky-placeholder');
        if (placeholder) {
          placeholder.remove();
        }
      }
    };

    // Create an intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        // Get the active filter element
        const activeElement =
          accessoryType && accessoryType.style.display === 'block' ? accessoryType : useCase;

        // Update sticky state based on intersection
        if (activeElement) {
          const isIntersecting = entries[0].isIntersecting;
          updateStickyState(isIntersecting, activeElement);
        }
      },
      {
        // Set the root margin to the negative height of the announcement bar
        // This will trigger when the bottom of the announcement bar crosses the top of the observed element
        rootMargin: announcementBar ? `-${announcementBar.clientHeight}px 0px 0px 0px` : '0px',
        threshold: 0,
      }
    );

    // Observe the product type element if it exists
    if (productType) {
      observer.observe(productType);
    }
  }

  static initFilterToggles() {
    // Logic for toggling use-case and accessory-type visibility
    const toggleFilters = (selectedProperty) => {
      const selectedFacet = selectedProperty.getAttribute('data-property');
      const useCase = document.querySelector('.use-case');
      const accessoryType = document.querySelector('.accessory-type');
      const productType = document.querySelector('.product-type');

      // Check if any product-type checkbox is checked, if not, select the first one
      const productTypeCheckboxes = productType.querySelectorAll('.facet__input');
      const isAnyChecked = Array.from(productTypeCheckboxes).some((checkbox) => checkbox.checked);
      if (!isAnyChecked) {
        productTypeCheckboxes[0].checked = true;
      }

      if (
        selectedFacet === 'Accessories' ||
        selectedFacet === 'Accesorios' ||
        selectedFacet === 'Zubehör' ||
        selectedFacet === 'Accessoires'
      ) {
        useCase.style.display = 'none';
        const activeUseCase = useCase.querySelector('.facet__input:checked');
        if (activeUseCase) activeUseCase.checked = false;
        if (accessoryType) {
          accessoryType.style.display = 'block';
        }
        if (!selectedProperty.checked) {
          accessoryType.style.display = 'none';
          useCase.style.display = 'block';
        }
      } else {
        useCase.style.display = 'block';
        if (accessoryType) {
          const activeAccessoryType = accessoryType.querySelector('.facet__input:checked');
          if (activeAccessoryType) activeAccessoryType.checked = false;
          accessoryType.style.display = 'none';
        }
      }
    };

    // Logic for handling filter checkboxes
    const handleFilterCheckboxes = (checkboxSelector, onChangeCallback) => {
      const checkboxes = document.querySelectorAll(checkboxSelector);
      if (checkboxes.length > 0) {
        checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', function () {
            checkboxes.forEach((cb) => {
              if (cb !== checkbox) cb.checked = false;
            });
            if (onChangeCallback) onChangeCallback(checkbox);
          });
        });
      }
    };

    // Handle product-type checkbox changes
    const productTypeCheckboxes = document.querySelectorAll('.product-type .facet__input');

    handleFilterCheckboxes('.product-type .facet__input', (selectedCheckbox) => {
      toggleFilters(selectedCheckbox);
    });

    // Handle use-case and accessory-type filters (ensure only one can be selected at a time)
    handleFilterCheckboxes('.use-case .facet__input');
    handleFilterCheckboxes('.accessory-type .facet__input');

    // Ensure the first product-type checkbox is selected if none are checked
    const firstProductCheckbox = productTypeCheckboxes[0];

    const checkFirstProductTypeIfNoneSelected = () => {
      if (productTypeCheckboxes.length > 0) {
        const isAnyChecked = Array.from(productTypeCheckboxes).some((checkbox) => checkbox.checked);
        if (!isAnyChecked) {
          firstProductCheckbox.checked = true;
          toggleFilters(firstProductCheckbox); // Ensure filters are toggled on load
        } else {
          // Ensure the currently checked filter applies its property
          const checkedCheckbox = Array.from(productTypeCheckboxes).find(
            (checkbox) => checkbox.checked
          );
          if (checkedCheckbox) {
            toggleFilters(checkedCheckbox); // Toggle filters based on the checked item
          }
        }
      }
    };

    // Ensure the first checkbox is selected on page load and toggle filters
    checkFirstProductTypeIfNoneSelected();
  }

  static initFacetClickListeners() {
    const allFacets = document.querySelectorAll('.facet__label');

    allFacets.forEach((facet) => {
      facet.addEventListener('click', () => {
        const counterWrapper = document.querySelector('.product-type .facet__input:checked');
        FacetFiltersForm.animateCards();
        if (counterWrapper) {
          const counterPlaceholder = counterWrapper.parentElement.querySelector('.facet__count');
          if (counterPlaceholder) {
            // Create a span element with the class 'spinning-loader'
            const spinner = document.createElement('span');
            spinner.className = 'spinning-loader';
            spinner.style.display = 'flex';

            // Set the spinner inside counterPlaceholder
            counterPlaceholder.innerHTML = '';
            counterPlaceholder.appendChild(spinner);
          }
        }
      });
    });
  }

  static animateCards() {
    try {
      // Select all card and wrapper elements
      const cards = document.querySelectorAll('.card--standard.card--media');
      const cardsWrapper = document.querySelectorAll('.card-wrapper.underline-links-hover');

      if (!cards.length) {
        console.warn('No cards found with the class .card--standard.card--media');
      }

      if (!cardsWrapper.length) {
        console.warn('No card wrappers found with the class .card-wrapper.underline-links-hover');
      }

      // Add animations to the cards
      cards.forEach((card) => {
        if (card.classList.contains('animate-in')) {
          card.classList.remove('animate-in');
        }
        card.classList.add('animate-out');
      });

      // Add skeleton loading to the wrappers after a delay
      setTimeout(() => {
        cardsWrapper.forEach((cardWrapper) => {
          cardWrapper.classList.add('skeleton_loading');
        });
      }, 300);
    } catch (error) {
      console.error('An error occurred while animating cards:', error);
    }
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderUpdatedProductGridContainer(html);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const data = FacetFiltersForm.filterData.find(filterDataUrl);
    if (data && data.html) {
      FacetFiltersForm.renderFilters(data.html, event);
      FacetFiltersForm.renderUpdatedProductGridContainer(data.html);
    }
  }

  static renderUpdatedProductGridContainer(html) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      const parser = new DOMParser();
      const parsedHTML = parser.parseFromString(html, 'text/html');
      const newGridHTML = parsedHTML.getElementById('ProductGridContainer')?.innerHTML;

      if (newGridHTML) {
        gridContainer.innerHTML = newGridHTML;
      }
    }

    // Update the product count dynamically after grid load
    FacetFiltersForm.updateProductCount();
    if (window.initiateVariantSwatches) {
      window.initiateVariantSwatches();
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElements = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.dataset.index === jsFilter.dataset.index : false;
    };

    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );
    facetsToRender.forEach((element) => {
      const targetElement = document.querySelector(
        `.js-filter[data-index="${element.dataset.index}"]`
      );
      if (targetElement) {
        targetElement.innerHTML = element.innerHTML;
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (activeFacetsElement && targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const elementSelectors = ['.sorting'];

    elementSelectors.forEach((selector) => {
      const element = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (element && targetElement) {
        targetElement.innerHTML = element.innerHTML;
      }
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector('.facets__selected');
    const sourceElement = source.querySelector('.facets__selected');
    const targetElementAccessibility = target.querySelector('.facets__summary');
    const sourceElementAccessibility = source.querySelector('.facets__summary');

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML;
    }

    if (sourceElementAccessibility && targetElementAccessibility) {
      targetElementAccessibility.outerHTML = sourceElementAccessibility.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const forms = [];

    sortFilterForms.forEach((form) => {
      if (
        form.id === 'FacetSortForm' ||
        form.id === 'FacetFiltersForm' ||
        form.id === 'FacetSortDrawerForm'
      ) {
        forms.push(this.createSearchParams(form));
      }
    });
    this.onSubmitForm(forms.join('&'), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') === -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) =>
      element.addEventListener('change', this.onRangeChange.bind(this))
    );
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];

    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
